import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { toast } from "react-toastify";

import { DownloadIcon, PlusIcon } from "../../assets/icons";
import itemImage from "../../assets/images/inventory-item.png";
import {
  Badge,
  Button,
  DataRow,
  PageTitle,
  Section,
  Loading,
} from "../../components";
import { NumberInput } from "../../components/forms";
import { ItemFilters } from "../../components/ItemFilters/ItemFilters";
import { enums } from "../../constants/enum";
import useItemService from "../../services/api/itemService";
import useInventoryService from "../../services/api/inventoryService";
import "./InventoryPage.scss";

function AddNewStockPage() {
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get("itemId");

  const [filters, setFilters] = useState({
    search: "",
    itemCode: itemId || "",
    sph: "",
    cyl: "",
    add: "",
    lenseDiagonal: "",
    bridge: "",
    armLength: "",
    accessoryType: "",
    material: "",
    color: "",
    gender: "",
    modelNumber: "",
    sunglassFrameColor: "",
    sunglassLenseColor: "",
    lenseType: "",
    minPrice: "",
    maxPrice: "",
    sort: enums.ITEM_SORTING_OPTIONS.NEWEST_FIRST,
    stockStatus: enums.ITEM_FILTER_STOCK_STATUS.ALL,
    category: enums.ITEM_FILTER_CATEGORIES.ALL,
  });
  const [selectedItems, setSelectedItems] = useState([]);

  const { ref, inView } = useInView();
  const { getAllItems, getItemById, queryKey } = useItemService();
  const { updateInventory } = useInventoryService();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateInventory,
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey]);
    },
  });

  const { data, isLoading, error, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [queryKey, filters],
      queryFn: async ({ pageParam = 1 }) => {
        if (filters.itemCode) {
          const item = await getItemById(filters.itemCode);
          return { items: [item], totalPages: 1, currentPage: 1 };
        }
        return getAllItems({ ...filters, page: pageParam });
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
      retry: 1,
    });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  function makeBadgeVariant(itemCategory) {
    switch (itemCategory) {
      case enums.ITEM_CATEGORIES.FRAME:
        return "info";
      case enums.ITEM_CATEGORIES.LENSE:
        return "success";
      case enums.ITEM_CATEGORIES.ACCESSORIES:
        return "warning";
      case enums.ITEM_CATEGORIES.SUNGLASS:
        return "danger";
      default:
        return "info";
    }
  }

  if (error) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
  }

  function addItemToSelectedItems(item) {
    setSelectedItems((prev) => {
      if (prev.some((i) => i._id === item._id)) return prev;
      return [
        ...prev,
        { ...item, addedStoreRoomStock: 0, addedDisplayStock: 0 },
      ];
    });
  }

  function handleStockChange(id, type, value) {
    setSelectedItems((prev) =>
      prev.map((item) => {
        if (item._id === id) {
          return {
            ...item,
            [type === "store" ? "addedStoreRoomStock" : "addedDisplayStock"]:
              value,
          };
        }
        return item;
      }),
    );
  }

  async function handleSubmit() {
    try {
      const promises = selectedItems.map((item) => {
        const newStoreRoomStock =
          (item.inventory.storeRoomStock || 0) + item.addedStoreRoomStock;
        const newDisplayStock =
          (item.inventory.displayStock || 0) + item.addedDisplayStock;
        return mutation.mutateAsync({
          id: item._id,
          payload: {
            storeRoomStock: newStoreRoomStock,
            displayStock: newDisplayStock,
          },
        });
      });

      await Promise.all(promises);
      toast.success("Stock updated successfully");
      setSelectedItems([]);
      queryClient.invalidateQueries([queryKey]);
    } catch (err) {
      toast.error(
        err?.response?.data?.payload?.message || "Failed to update stock",
      );
    }
  }

  return (
    <div className="page add-new-stock-page">
      <PageTitle text="Add New Stock" prev="/inventory" />

      <Section title="Select Items">
        <ItemFilters
          initialValues={filters}
          onSubmit={(values) => {
            setFilters(values);
          }}
          onClear={() => {
            setFilters({
              search: "",
              itemCode: "",
              sph: "",
              cyl: "",
              add: "",
              lenseDiagonal: "",
              bridge: "",
              armLength: "",
              accessoryType: "",
              material: "",
              color: "",
              gender: "",
              modelNumber: "",
              sunglassFrameColor: "",
              sunglassLenseColor: "",
              lenseType: "",
              minPrice: "",
              maxPrice: "",
              sort: enums.ITEM_SORTING_OPTIONS.NEWEST_FIRST,
              stockStatus: enums.ITEM_FILTER_STOCK_STATUS.ALL,
              category: enums.ITEM_FILTER_CATEGORIES.ALL,
            });
          }}
          withItemCode
        />

        <div className="inventory-list">
          {isLoading ? (
            <Loading variant="inline" isLoading={true} />
          ) : (
            data?.pages.map(({ items }) =>
              items.map((item) => (
                <div className="item-card" key={item._id}>
                  <div className="item-img">
                    <div className="img-bx">
                      <img src={itemImage} alt="Item" />
                    </div>
                  </div>

                  <div className="item-meta">
                    <div className="item-name">
                      <i className="item-brand-letter">T</i>
                      <p>{item.itemName}</p>
                    </div>

                    <div className="item-stats">
                      <div>
                        <Badge
                          className="badge"
                          text={item.category}
                          variant={makeBadgeVariant(item.category)}
                          outlined
                        />
                        <div className="separator"></div>
                        <Badge
                          className="badge"
                          text={item.inventory?.stockStatus}
                        />
                      </div>

                      <div className="separator"></div>

                      <div>
                        <p>Storeroom Stock: {item.inventory?.storeRoomStock}</p>
                        <div className="separator"></div>
                        <p>Showcase Stock: {item.inventory?.displayStock}</p>
                      </div>
                    </div>
                  </div>

                  <div className="item-actions">
                    <Button
                      text="Add Item"
                      icon={<PlusIcon />}
                      onClick={() => addItemToSelectedItems(item)}
                    />
                  </div>
                </div>
              )),
            )
          )}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            ref={ref}
          >
            {isFetchingNextPage && (
              <Loading isLoading={true} variant="inline" />
            )}
          </div>
        </div>
      </Section>

      {selectedItems.length > 0 ? (
        <Section title="Add Stock Data">
          {selectedItems.map((item) => (
            <div className="item-stock-change" key={item._id}>
              <div className="item-details">
                <div className="item-img">
                  <div className="img-bx">
                    <img src={itemImage} alt="Item" />
                  </div>
                </div>

                <div className="item-meta">
                  <div className="item-name">
                    <i className="item-brand-letter">
                      {item.brand ? item.brand.split("")[0] : "T"}
                    </i>
                    <p>{item.itemName}</p>
                  </div>

                  <div className="item-stats">
                    <div>
                      <Badge
                        className="badge"
                        text={item.category}
                        variant={makeBadgeVariant(item.category)}
                        outlined
                      />
                      <div className="separator"></div>
                      <Badge
                        className="badge"
                        text={item.inventory?.stockStatus}
                      />
                    </div>

                    <div className="separator"></div>

                    <div>
                      <p>Storeroom Stock: {item.inventory?.storeRoomStock}</p>
                      <div className="separator"></div>
                      <p>Showcase Stock: {item.inventory?.displayStock}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="item-data-section">
                <p>Current Stock</p>
                <DataRow
                  labelLeft="Storeroom Stock"
                  dataLeft={
                    <div className="item-detail-centered">
                      {item.inventory?.storeRoomStock || 0}
                    </div>
                  }
                  labelRight="Showcase Stock"
                  dataRight={
                    <div className="item-detail-centered">
                      {item.inventory?.displayStock || 0}
                    </div>
                  }
                />
              </div>

              <div className="item-data-section">
                <p>
                  Add New Stock{" "}
                  <span>
                    (The numbers you input here will be added to the current
                    stock.)
                  </span>
                </p>
                <DataRow
                  labelLeft="Storeroom Stock"
                  dataLeft={
                    <NumberInput
                      value={item.addedStoreRoomStock}
                      onChange={(v) => handleStockChange(item._id, "store", v)}
                    />
                  }
                  labelRight="Showcase Stock"
                  dataRight={
                    <NumberInput
                      value={item.addedDisplayStock}
                      onChange={(v) =>
                        handleStockChange(item._id, "display", v)
                      }
                    />
                  }
                />
              </div>

              <div className="item-data-section">
                <p>
                  Stock after adding the new stock{" "}
                  <span>(This will be the updated stock count.)</span>
                </p>
                <DataRow
                  labelLeft="Storeroom Stock"
                  dataLeft={
                    <div className="item-detail-centered">
                      {(item.inventory?.storeRoomStock || 0) +
                        item.addedStoreRoomStock}
                    </div>
                  }
                  labelRight="Showcase Stock"
                  dataRight={
                    <div className="item-detail-centered">
                      {(item.inventory?.displayStock || 0) +
                        item.addedDisplayStock}
                    </div>
                  }
                />
              </div>
            </div>
          ))}
          <Button
            text="Submit"
            icon={<DownloadIcon />}
            onClick={handleSubmit}
            disabled={mutation.isPending}
          />
        </Section>
      ) : null}
      <Loading isLoading={mutation.isPending} variant="fullScreen" />
    </div>
  );
}

export default AddNewStockPage;
